import React, { useState, useCallback, useEffect, useRef } from 'react'
import {
  Box, Typography, IconButton, Tooltip, Divider, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, FormControlLabel, RadioGroup, Radio, Switch,
} from '@mui/material'
import { FancyButton } from '../../comps/FancyButtons'
import RefreshIcon from '@mui/icons-material/Refresh'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import ClearAllIcon from '@mui/icons-material/ClearAll'
import RestoreIcon from '@mui/icons-material/Restore'
import AutosaveIcon from '../../comps/AutosaveIcon'
import BidBatchProcessModal from '../modals/BidBatchProcessModal'
import BidImportQuestionsModal from '../modals/BidImportQuestionsModal'
import BidProjectHistoryModal from '../modals/BidProjectHistoryModal'
import InputModal from '../modals/InputModal'
import FolderChip from '../../comps/FolderChip'
import ConfirmModal from '../modals/ConfirmModal'
import BidQuestionsTab from './BidQuestionsTab'
import BidScoringTab from './BidScoreTab'
import BidWinThemesTab from './BidWinThemesTab'
import BidManageTab from './BidManageTab'
import BidOverviewTab from './BidOverviewTab'
import TabHeader from '../../comps/TabHeader'
import CfpThreeDotsMenu from '../../comps/CfpThreeDotsMenu'
import StateDisplay from '../../comps/StateDisplay'
import DebugJsonButton from '../../comps/DebugJsonButton'
import { useAppActions, useAppProject } from '../../AppContext'
import { addToast } from '../../services/Toasts'
import { pmGetProject, pmDelProject, pmSaveProject, pmReport } from '../../services/ProjectManagerService'
import { BORDER_BLACK, ICON_MD, ICON_SM } from '../../common/theme'
import { downloadFile } from '../../common/downloadHelpers'
import { parseBidUrl } from '../../common/bidUrl'
import { questionAnchorId, type Project } from '../../types'
import TaClickZone from '../../comps/TaClickZone'
import { QUESTION_PAGE_SIZE } from '../../comps/BidQuestion'
import { DEBUG_MODE } from '../../common/common'

type ProjectTab = 'overview' | 'questions' | 'score' | 'winthemes' | 'manage'
type ManageSubTab = 'questions' | 'scoring' | 'winthemes' | 'bidsources'

const PROJECT_TABS: ProjectTab[] = ['overview', 'questions', 'score', 'winthemes', 'manage']
const MANAGE_SUBTABS: ManageSubTab[] = ['questions', 'scoring', 'winthemes', 'bidsources']

interface BidProjectViewProps {
  // project: Project
  onBack: () => void
  onProjectDeleted: () => void
}

export default function BidProjectView({
  // project: initialProject, 
  onBack, onProjectDeleted }: BidProjectViewProps) {

  console.log('BidProjectView render')
  const { currentProject } = useAppProject()
  const { setCurrentProject, updateProject, getCurrentStyleName } = useAppActions()
  // AppContext.currentProject is the single source of truth - never read
  // from `initialProject` after mount (it's a stale snapshot of whatever
  // BidProjectList passed) and never keep a local copy. `setProject` is a
  // thin shim that always goes through `setCurrentProject` so callers can
  // use a familiar setState shape.
  // const project = (currentProject ?? initialProject) as Project
  const setProject: React.Dispatch<React.SetStateAction<Project>> = (value: any) => {
    setCurrentProject(prev => (typeof value === 'function' ? value(prev as Project) : value))
  }

  // Initial values for tab + sub-tabs come from the URL so a pasted/back-button
  // link lands on the right place. Subsequent changes push back to the URL.
  const initialUrl = parseBidUrl()
  const initialProjectTab: ProjectTab =
    PROJECT_TABS.includes(initialUrl.tab as ProjectTab) ? (initialUrl.tab as ProjectTab) : 'overview'
  const initialManageSub: ManageSubTab =
    initialUrl.tab === 'manage' && MANAGE_SUBTABS.includes(initialUrl.sub as ManageSubTab)
      ? (initialUrl.sub as ManageSubTab) : 'questions'

  const [projectTab, setProjectTab] = useState<ProjectTab>(initialProjectTab)
  const [manageInitialSubTab, setManageInitialSubTab] = useState<ManageSubTab>(initialManageSub)
  const [stateFilter, setStateFilter] = useState<string | undefined>(undefined)
  const [sectionFilter, setSectionFilter] = useState<string | undefined>(undefined)
  const [currentPage, setCurrentPage] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  // Seed from the ref-backed getter at mount - no subscription, so we
  // don't re-render every time another tab flips the global style.
  const [projectStyleName, setProjectStyleName] = useState(() => getCurrentStyleName() ?? '')
  const [batchProcessOpen, setBatchProcessOpen] = useState(false)
  const [batchQuestionList, setBatchQuestionList] = useState<any[]>([])

  const [renameOpen, setRenameOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [clearAnswersOpen, setClearAnswersOpen] = useState(false)
  const [deleteAllOpen, setDeleteAllOpen] = useState(false)
  const [refreshConfirmOpen, setRefreshConfirmOpen] = useState(false)
  const [exportOpen, setExportOpen] = useState(false)
  const [exportFormat, setExportFormat] = useState<'docx' | 'excel'>('docx')
  const [isExporting, setIsExporting] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [autosaveOn, setAutosaveOn] = useState(true)
  // Tracks the wall-clock ms of the last successful save. Drives the
  // "last saved at HH:MM" indicator next to the autosave toggle. Reset
  // when the user switches projects so the label doesn't carry forward
  // a stale time from a different bid.
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null)
  // Refs that the autosave interval reads at tick time - keeping these
  // out of the interval's dep list stops the timer being reset on every
  // keystroke (handleSaveBid changes identity per render).
  const projectDirtyRef = useRef(false)
  const savingBidRef = useRef(false)
  const saveBidRef = useRef<() => Promise<void> | void>(() => { })

  useEffect(() => {
    fetchProject()
    setLastSavedAt(null)
  }, [currentProject?.projectsha])
  const fetchProject = useCallback(async () => {
    setIsLoading(true)
    try {
      const cur = currentProject as any
      const needsHydration = !cur || cur.projectsha !== currentProject?.projectsha
        || cur.partial || cur.questions === undefined
      if (needsHydration) {
        const result = await pmGetProject(currentProject?.projectsha)
        if (result) {
          setProject(result)
        }
      }
    } catch {
      addToast('error', 'Failed to load bid details.')
    } finally {
      setIsLoading(false)
    }
  }, [currentProject?.projectsha, addToast])

  // Bid is "dirty" if either the project itself or any of its questions
  // have unsaved local edits. pmSaveProject persists the questions array
  // too, so one button covers both. Single source of truth for the Save
  // indicator, the refresh-confirm gate, and the autosave tick.
  const projectDirty = !!currentProject?.modified
    || (currentProject?.questions ?? []).some((q: any) => q.modified)
  projectDirtyRef.current = projectDirty

  // Refresh - if there are unsaved local changes (e.g. Clear All Answers /
  // Delete All Questions ran but haven't been saved), confirm before reloading.
  const handleRefreshClick = useCallback(() => {
    if (projectDirty) setRefreshConfirmOpen(true)
    else fetchProject()
  }, [projectDirty, fetchProject])

  const confirmRefresh = useCallback(() => {
    setRefreshConfirmOpen(false)
    fetchProject()
  }, [fetchProject])

  // URL syncing for the project tab / sub-tab was removed alongside the
  // project slug. AppContext.currentProject is now the single source of
  // truth across surfaces; tab state lives in this component's React state.

  // Streaming event subscription used to live here too - it now lives in
  // AppContext (single subscriber), which patches currentProject directly.

  const [savingBid, setSavingBid] = useState(false)
  const handleSaveBid = useCallback(async () => {
    setSavingBid(true)
    try {
      const result = await pmSaveProject(currentProject)
      if (result) setProject(result)
      // pmSaveProject persists every question too, so wipe their dirty
      // flags alongside the project's.
      setCurrentProject(prev => (prev ? {
        ...prev,
        modified: false,
        questions: (prev.questions ?? []).map((q: any) => (q.modified ? { ...q, modified: false } : q)),
      } : prev))
      setLastSavedAt(Date.now())
      if (result?.success)
        addToast('success', 'Bid saved')
      else
        addToast('error', 'Bid save failed')
    } catch {
      addToast('error', 'Failed to save bid.')
    } finally {
      setSavingBid(false)
    }
  }, [currentProject, addToast, setCurrentProject])

  // Autosave: when toggled on, fire every 3 minutes if the bid is dirty.
  // The refs above hold the latest dirty + save-handler values so the
  // interval doesn't churn on every keystroke (handleSaveBid's identity
  // changes whenever currentProject does).
  useEffect(() => { saveBidRef.current = handleSaveBid }, [handleSaveBid])
  useEffect(() => { savingBidRef.current = savingBid }, [savingBid])
  useEffect(() => {
    if (!autosaveOn) return
    const id = setInterval(() => {
      if (projectDirtyRef.current && !savingBidRef.current) saveBidRef.current()
    }, 3 * 60 * 1000)
    return () => clearInterval(id)
  }, [autosaveOn])

  const handleRenameBid = useCallback(async (name: string) => {
    if (!name.trim() || !currentProject) return
    const updated = { ...currentProject, name: name.trim() }
    setProject(updated)
    try {
      const result = await pmSaveProject(updated)

      if (result) setProject(result)
      // Rename is persisted directly so we don't leave the project dirty.
      updateProject({ modified: false })
      if (result?.success)
        addToast('success', 'Bid renamed')
      else
        addToast('success', 'Bid rename failed')
    } catch {
      addToast('error', 'Failed to rename bid.')
    }
  }, [currentProject, addToast, updateProject])

  const handleExport = useCallback(async () => {
    setIsExporting(true)
    setExportOpen(false)
    try {
      const result = await pmReport(currentProject, exportFormat)
      if (result?.doc) downloadFile({ data: result.doc, base64: true, filename: result.filename })
      addToast('success', 'Document downloaded')
    } catch {
      addToast('error', 'Failed to export bid.')
    } finally {
      setIsExporting(false)
    }
  }, [currentProject, exportFormat, addToast])

  const handleDelete = useCallback(async () => {
    try {
      await pmDelProject(currentProject?.projectsha)
      onProjectDeleted()
    } catch {
      addToast('error', 'Failed to delete bid.')
    }
  }, [currentProject?.projectsha, onProjectDeleted, addToast])

  const handleClearAnswers = useCallback(() => {
    setProject(p => ({
      ...p,
      questions: (p.questions ?? []).map((q: any) => ({
        ...q,
        answerobj: null,
        bideval: null,
        structure: null,
        sourcedocs: [],
        structuretweaks: [],
      })),
      modified: true,
    }))
  }, [])

  const handleDeleteAllQuestions = useCallback(() => {
    setProject(p => ({ ...p, questions: [], modified: true }))
  }, [])

  const handleQuestionChange = useCallback((updated: any) => {
    setProject(p => ({
      ...p,
      questions: (p.questions ?? []).map((q: any) =>
        q.questionsha === updated.questionsha ? { ...q, ...updated } : q,
      ),
    }))
  }, [])

  const openBatchProcess = useCallback((qs?: any[]) => {
    setBatchQuestionList(qs && qs.length > 0 ? qs : (currentProject?.questions ?? []))
    setBatchProcessOpen(true)
  }, [currentProject])

  const toggleStateFilter = (s: string) =>
    setStateFilter(prev => prev === s ? undefined : s)

  const filteredQuestions = (currentProject?.questions ?? []).filter((q: any) => {
    if (stateFilter && q.state !== stateFilter) return false
    if (sectionFilter !== undefined && (q.section ?? '') !== sectionFilter) return false
    return true
  })

  const handleSectionChange = useCallback((s: string | undefined) => {
    setSectionFilter(s)
    setCurrentPage(1)
  }, [])

  const totalQPages = Math.ceil(filteredQuestions.length / QUESTION_PAGE_SIZE)
  const pagedQuestions = filteredQuestions.slice(
    (currentPage - 1) * QUESTION_PAGE_SIZE,
    currentPage * QUESTION_PAGE_SIZE,
  )

  const menuItems = [
    { label: 'Save Bid', icon: <SaveOutlinedIcon sx={{ fontSize: ICON_SM }} />, onClick: handleSaveBid },
    { label: 'Rename Bid', icon: <DriveFileRenameOutlineIcon sx={{ fontSize: ICON_SM }} />, onClick: () => setRenameOpen(true) },
    { label: 'Export', icon: <FileDownloadOutlinedIcon sx={{ fontSize: ICON_SM }} />, onClick: () => setExportOpen(true) },
    { label: 'Upload Questions', icon: <CloudUploadOutlinedIcon sx={{ fontSize: ICON_SM }} />, onClick: () => setImportOpen(true) },
    { label: 'Roll Back to Previous Version', icon: <RestoreIcon sx={{ fontSize: ICON_SM }} />, onClick: () => setHistoryOpen(true) },
    { label: 'Clear All Workflows', icon: <ClearAllIcon sx={{ fontSize: ICON_SM }} />, onClick: () => setClearAnswersOpen(true), danger: true },
    { label: 'Delete All Questions', icon: <DeleteOutlineIcon sx={{ fontSize: ICON_SM }} />, onClick: () => setDeleteAllOpen(true), danger: true },
    { label: 'Delete Bid', icon: <DeleteOutlineIcon sx={{ fontSize: ICON_SM }} />, onClick: () => setDeleteOpen(true), danger: true, dividerAbove: true },
  ]

  return (
    <Box data-testid="bpv_root" sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', }}>

      {currentProject &&
        (<>  <BidBatchProcessModal
          open={batchProcessOpen}
          projectsha={currentProject?.projectsha}
          questions={batchQuestionList}
          projectbideval={currentProject?.projectbideval}
          defaultStyleName={projectStyleName}
          onClose={() => setBatchProcessOpen(false)}
        />

          <BidImportQuestionsModal
            open={importOpen}
            projectsha={currentProject?.projectsha}
            onClose={() => setImportOpen(false)}
            onImported={fetchProject}
          />
          <BidProjectHistoryModal
            open={historyOpen}
            projectsha={currentProject?.projectsha}
            onClose={() => setHistoryOpen(false)}
          />
          <InputModal
            open={renameOpen}
            title="Rename Bid"
            label="Bid name"
            initialValue={currentProject?.name}
            singleLine
            onConfirm={handleRenameBid}
            onClose={() => setRenameOpen(false)}
          />

          <ConfirmModal
            open={deleteOpen}
            title="Delete Bid"
            body={`Are you sure you want to delete "${currentProject?.name}"? This cannot be undone.`}
            okLabel="Delete"
            onConfirm={handleDelete}
            onClose={() => setDeleteOpen(false)}
          />

          <ConfirmModal
            open={clearAnswersOpen}
            title="Clear All Answers"
            body="This will remove all generated answers, scores and sources from every question, but keep the questions themselves."
            okLabel="Clear Answers"
            onConfirm={handleClearAnswers}
            onClose={() => setClearAnswersOpen(false)}
          />

          <ConfirmModal
            open={deleteAllOpen}
            title="Delete All Questions"
            body="This will permanently delete all questions from this bid."
            okLabel="Delete All"
            onConfirm={handleDeleteAllQuestions}
            onClose={() => setDeleteAllOpen(false)}
          />

          <ConfirmModal
            open={refreshConfirmOpen}
            title="Discard unsaved changes?"
            body="Refreshing reloads this bid from the server and will discard any unsaved local changes."
            okLabel="Refresh"
            testIdPrefix="bpv_refreshconfirm"
            onConfirm={confirmRefresh}
            onClose={() => setRefreshConfirmOpen(false)}
          />
        </>
        )}
      <Dialog open={exportOpen} onClose={() => setExportOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ 'data-testid': 'bpv_export_dlg' } as any}>
        <DialogTitle sx={{ pb: 1, fontSize: '1rem' }}>Export Bid</DialogTitle>
        <DialogContent>
          <FormControl>
            <RadioGroup value={exportFormat} onChange={(_, v) => setExportFormat(v as 'docx' | 'excel')}>
              <FormControlLabel value="docx" control={<Radio size="small" inputProps={{ 'data-testid': 'bpv_export_docx' } as any} />} label="Word (DocX)" />
              <FormControlLabel value="excel" control={<Radio size="small" inputProps={{ 'data-testid': 'bpv_export_excel' } as any} />} label="Excel" />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <FancyButton testId="bpv_export_cancel_btn" onClick={() => setExportOpen(false)}>Cancel</FancyButton>
          <FancyButton testId="bpv_export_confirm_btn" solid onClick={handleExport} disabled={isExporting}>
            {isExporting ? 'Exporting...' : 'Export'}
          </FancyButton>
        </DialogActions>
      </Dialog>

      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0, gap: 0.5 }}>
        <Tooltip title="Back to Bids">
          <IconButton data-testid="bpv_back_btn" size="small" onClick={onBack}>
            <ArrowBackIcon sx={{ fontSize: ICON_MD }} />
          </IconButton>
        </Tooltip>
        <FolderChip
          name={currentProject?.name ?? ""}
          isProject
          bold={true}
          noHover={true}
          testId="bpv_project_chip"
        />
        {DEBUG_MODE && (
          <DebugJsonButton
            value={currentProject}
            title={`Project JSON · ${currentProject?.projectsha ?? ''}`}
            tooltip="Inspect project JSON (debug)"
            testIdPrefix="bpv_debug"
          />
        )}
        <Tooltip title="Refresh bid">
          <span>
            <IconButton data-testid="bpv_refresh_btn" size="small" onClick={handleRefreshClick} disabled={isLoading}>
              {isLoading ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: ICON_MD }} />}
            </IconButton>
          </span>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <StateDisplay
          questions={currentProject?.questions ?? []}
          stateFilter={stateFilter}
          onStateClick={toggleStateFilter}
          onClearFilter={() => setStateFilter(undefined)}
        />
        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
        <CfpThreeDotsMenu items={menuItems} />
      </Box>

      {/* Tab bar */}
      <TabHeader<ProjectTab>
        size="large"
        tabs={[
          { key: 'overview', label: 'Overview' },
          { key: 'questions', label: 'Questions' },
          { key: 'score', label: 'Score' },
          { key: 'winthemes', label: 'Win Themes' },
          { key: 'manage', label: 'Manage' },
          //{ key: 'manage', label: <SettingsOutlinedIcon sx={{ fontSize: ICON_MD }} /> },
        ]}
        active={projectTab}
        onChange={setProjectTab}
        endSlot={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>

            {!autosaveOn && (
              <Tooltip title={savingBid ? 'Saving...' : projectDirty ? 'Save bid (unsaved changes)' : 'No changes to save'}>
                <span>
                  <IconButton
                    data-testid="bpv_save_btn"
                    size="small"
                    onClick={handleSaveBid}
                    sx={{ color: projectDirty ? 'primary.main' : undefined }}
                  >
                    {savingBid ? <CircularProgress size={14} /> : <SaveOutlinedIcon sx={{ fontSize: ICON_MD }} />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title={
              autosaveOn
                ? (savingBid ? 'Autosaving...' : 'Autosave every 3 min. Click to save now')
                : 'Autosave off. Toggle on to autosave every 3 min.'
            }>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {autosaveOn && <AutosaveIcon
                  onClick={handleSaveBid}
                  sx={{ fontSize: ICON_MD, opacity: 0.9, cursor: 'pointer' }} />}
                {autosaveOn && savingBid && <CircularProgress size={12} sx={{ ml: 0.25 }} />}
                <Switch
                  data-testid="bpv_autosave_switch"
                  size="small"
                  checked={autosaveOn}
                  onChange={(e) => setAutosaveOn(e.target.checked)}
                />
                {lastSavedAt != null && (
                  <Typography
                    data-testid="bpv_last_saved"
                    variant="caption"
                    sx={{ fontSize: '0.68rem', opacity: 0.6, ml: 0.25, minWidth: 32, whiteSpace: 'nowrap' }}
                  >
                    {new Date(lastSavedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
                  </Typography>
                )}
              </Box>
            </Tooltip>
            <Tooltip title="Batch process all questions">
              <span>
                <IconButton
                  data-testid="bpv_batchprocess_btn"
                  size="small"
                  onClick={() => openBatchProcess()}
                  disabled={!(currentProject?.questions?.length)}
                  sx={{ color: 'primary.main' }}
                >
                  <PlayCircleOutlineIcon sx={{ fontSize: ICON_MD }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        }
      />

      {(isLoading || currentProject?.partial) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {/* Empty state - covers both the Overview and Questions tabs when the
          bid has no questions yet. Replaces BidOverviewTab on the Overview
          tab in this case since there's nothing to summarise. */}
      {!isLoading && !currentProject?.partial
        && (projectTab === 'overview' || projectTab === 'questions')
        && (currentProject?.questions?.length ?? 0) === 0 && (
          <TaClickZone
            testId={'bpv_empty_state'}
            onClick={() => setImportOpen(true)}
            icon={<CloudUploadOutlinedIcon sx={{ fontSize: 48, opacity: 0.5 }} />}
            label="Import Questions"
            subLabel="This bid has no questions yet. Click to import a questionnaire document and get started."
            sx={{
              m: 1,
              height: '100%',
              gap: 1.5,
              px: 5, py: 4,
            }}
          />

        )}

      {!isLoading && !currentProject?.partial && projectTab === 'overview' && (currentProject?.questions?.length ?? 0) > 0 && (
        <BidOverviewTab
          project={currentProject}
          onSelectQuestion={(q) => {
            setProjectTab('questions')
            // Navigate to the page containing this question, then anchor-scroll to it.
            const all = filteredQuestions
            const idx = all.findIndex((x: any) => x.questionsha === (q as any).questionsha)
            if (idx >= 0) setCurrentPage(Math.floor(idx / QUESTION_PAGE_SIZE) + 1)
            setTimeout(() => {
              const el = document.getElementById(questionAnchorId((q as any).questionsha))
              el?.scrollIntoView({ behavior: 'auto', block: 'start' })
            }, 80)
          }}
          onGoToBidSources={() => {
            setManageInitialSubTab('bidsources')
            setProjectTab('manage')
          }}
          onGoToWinThemes={() => {
            setManageInitialSubTab('winthemes')
            setProjectTab('manage')
          }}
          onGoToScoringTemplates={() => {
            setManageInitialSubTab('scoring')
            setProjectTab('manage')
          }}
        />
      )}

      {!isLoading && !currentProject?.partial && projectTab === 'questions' && (currentProject?.questions?.length ?? 0) > 0 && currentProject && (
        <BidQuestionsTab
          project={currentProject}
          allFilteredQuestions={filteredQuestions}
          pagedQuestions={pagedQuestions}
          totalQuestions={filteredQuestions.length}
          currentPage={currentPage}
          totalPages={totalQPages}
          onPageChange={setCurrentPage}
          stateFilter={stateFilter}
          sectionFilter={sectionFilter}
          onSectionChange={handleSectionChange}
          onGenerate={(q) => openBatchProcess([q])}
          onQuestionChange={handleQuestionChange}
          onBatchProcess={(qs) => openBatchProcess(qs)}
        />
      )}

      {!isLoading && !currentProject?.partial && projectTab === 'score' && currentProject && (
        <BidScoringTab
          project={currentProject}
          allFilteredQuestions={filteredQuestions}
          pagedQuestions={pagedQuestions}
          totalQuestions={filteredQuestions.length}
          currentPage={currentPage}
          totalPages={totalQPages}
          onPageChange={setCurrentPage}
          stateFilter={stateFilter}
          sectionFilter={sectionFilter}
          onSectionChange={handleSectionChange}
          onBatchProcess={(qs) => openBatchProcess(qs)}
          onQuestionChange={handleQuestionChange}
        />
      )}

      {!isLoading && !currentProject?.partial && projectTab === 'winthemes' && currentProject && (
        <BidWinThemesTab
          project={currentProject}
          allFilteredQuestions={filteredQuestions}
          pagedQuestions={pagedQuestions}
          totalQuestions={filteredQuestions.length}
          currentPage={currentPage}
          totalPages={totalQPages}
          onPageChange={setCurrentPage}
          stateFilter={stateFilter}
          sectionFilter={sectionFilter}
          onSectionChange={handleSectionChange}
          onBatchProcess={(qs) => openBatchProcess(qs)}
          onQuestionChange={handleQuestionChange}
        />
      )}

      {!isLoading && !currentProject?.partial && projectTab === 'manage' && (
        <BidManageTab
          project={currentProject}
          pagedQuestions={pagedQuestions}
          totalQuestions={filteredQuestions.length}
          currentPage={currentPage}
          totalPages={totalQPages}
          onPageChange={setCurrentPage}
          onRefresh={fetchProject}
          stateFilter={stateFilter}
          onQuestionChange={handleQuestionChange}
          onProjectSave={(updated) => setProject(updated)}
          subTab={manageInitialSubTab}
          onSubTabChange={setManageInitialSubTab}
          projectStyleName={projectStyleName}
          onProjectStyleNameChange={setProjectStyleName}
        />
      )}

    </Box>
  )
}
